import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, getApiKey, formatMs, type ModelRow, type ProviderView, type RequestDetail } from "../lib/api";
import {
  curlSnippet,
  getDefaultModel,
  jsSnippet,
  loadRuntimeConfig,
  pySnippet,
  type RuntimeConfig,
  isPolicyModel,
} from "../lib/developer";
import { isDemoModel, recommendedScore, type ModelView } from "../lib/model-display";
import { ModelSelector } from "../components/ModelSelector";
import {
  CopyButton,
  Dot,
  EmptyState,
  PageHeader,
  ProviderMark,
  SectionHeader,
  SkeletonRows,
} from "../components/ui";

interface RouteInfo {
  requestId: string | null;
  traceId: string | null;
  providerId: string | null;
  providerModelId: string | null;
  cacheHit: string | null;
  latencyMs: number;
  tokens: { in: number | null; out: number | null };
  // Enriched post-hoc from /api/requests/{id}
  row?: RequestDetail;
}

type Lang = "curl" | "js" | "py";

export default function PlaygroundPage() {
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [models, setModels] = useState<ModelView[]>([]);
  const [providers, setProviders] = useState<ProviderView[]>([]);
  const [loading, setLoading] = useState(true);

  const [model, setModel] = useState(searchParams.get("model") ?? getDefaultModel());
  const [prompt, setPrompt] = useState(
    "Explain what an AI inference gateway does in two sentences.",
  );
  const [maxTokens, setMaxTokens] = useState(200);
  const [temperature, setTemperature] = useState(0.7);
  const [stream, setStream] = useState(false);

  const [sending, setSending] = useState(false);
  const [output, setOutput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [route, setRoute] = useState<RouteInfo | null>(null);
  const [lang, setLang] = useState<Lang>("curl");

  useEffect(() => {
    Promise.all([
      loadRuntimeConfig(),
      api<{ data: ModelRow[] }>("/api/models"),
      api<{ data: ProviderView[] }>("/api/providers"),
    ])
      .then(([cfg, m, p]) => {
        setConfig(cfg);
        const pvMap = new Map(p.data.map((pv) => [pv.providerTypeId, pv]));
        setModels(
          m.data.map((row) => ({
            ...row,
            providerView: row.provider?.providerTypeId
              ? pvMap.get(row.provider.providerTypeId)
              : undefined,
          })),
        );
        setProviders(p.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const selectedProvider = useMemo(() => {
    if (!route?.providerId) return null;
    return providers.find((p) => p.id === route.providerId) ?? null;
  }, [route, providers]);

  const selectedModel = models.find((m) => m.logicalId === model);

  async function send() {
    setSending(true);
    setError(null);
    setOutput("");
    setRoute(null);
    const t0 = Date.now();
    try {
      const res = await fetch("/v1/chat/completions", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${getApiKey()}`,
        },
        body: JSON.stringify({
          model,
          messages: [{ role: "user", content: prompt }],
          max_tokens: maxTokens,
          temperature,
          stream,
        }),
      });

      const info: RouteInfo = {
        requestId: res.headers.get("x-request-id"),
        traceId: res.headers.get("x-trace-id"),
        providerId: res.headers.get("x-infermesh-provider"),
        providerModelId: res.headers.get("x-infermesh-model"),
        cacheHit: res.headers.get("x-infermesh-cache"),
        latencyMs: Date.now() - t0,
        tokens: { in: null, out: null },
      };

      if (!res.ok) {
        const text = await res.text();
        let msg = text;
        try {
          msg = JSON.parse(text)?.error?.message ?? text;
        } catch {}
        throw new Error(`${res.status} — ${msg}`);
      }

      if (stream) {
        const reader = res.body!.getReader();
        const dec = new TextDecoder();
        let buf = "";
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() ?? "";
          for (const line of lines) {
            const s = line.trim();
            if (!s.startsWith("data:")) continue;
            const payload = s.slice(5).trim();
            if (payload === "[DONE]") continue;
            try {
              const chunk = JSON.parse(payload);
              const delta = chunk.choices?.[0]?.delta?.content ?? "";
              if (delta) setOutput((o) => o + delta);
            } catch {}
          }
        }
        info.latencyMs = Date.now() - t0;
        setRoute(info);
      } else {
        const data = await res.json();
        info.latencyMs = Date.now() - t0;
        info.tokens = {
          in: data.usage?.prompt_tokens ?? null,
          out: data.usage?.completion_tokens ?? null,
        };
        setOutput(data.choices?.[0]?.message?.content ?? "(empty response)");
        setRoute(info);
      }

      // Enrich with the persisted request row (fallbacks, cost, reasons).
      if (info.requestId) {
        setTimeout(() => {
          api<RequestDetail>(`/api/requests/${info.requestId}`)
            .then((row) =>
              setRoute((r) => (r ? { ...r, row, latencyMs: row.latencyMs ?? r.latencyMs } : r)),
            )
            .catch(() => {});
        }, 400);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setSending(false);
    }
  }

  const snippetInput = { baseUrl: config?.publicBaseUrl ?? "", model, prompt, stream };
  const snippets: Record<Lang, { label: string; code: string }> = {
    curl: { label: "cURL", code: curlSnippet(snippetInput) },
    js: { label: "JavaScript", code: jsSnippet(snippetInput) },
    py: { label: "Python", code: pySnippet(snippetInput) },
  };

  const explanation = route?.row?.routingExplanation?.selected;
  const attempts = route?.row?.attempts ?? [];

  return (
    <div className="mx-auto max-w-6xl pb-16">
      <PageHeader
        title="API Playground"
        subtitle="Send a real request through NEXUS and inspect the route it took."
        right={
          <Link to="/keys" className="btn-ghost text-xs">
            Developer access →
          </Link>
        }
      />

      <div className="grid gap-5 lg:grid-cols-2">
        {/* ── Request ───────────────────────────────────────── */}
        <section className="panel p-4">
          <h2 className="mb-3 text-sm font-semibold">Request</h2>

          <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-canvas-muted">
            Model / routing policy
          </label>
          <ModelSelector
            models={models}
            providers={providers}
            value={model}
            onChange={setModel}
          />
          {isPolicyModel(model) ? (
            <p className="mt-1.5 text-[11px] text-canvas-muted">
              NEXUS routing policy — the best eligible deployment is chosen per request.
            </p>
          ) : selectedModel ? (
            <p className="mt-1.5 text-[11px] text-canvas-muted">
              {selectedModel.provider?.displayName} ·{" "}
              {selectedModel.pricing.tier === "free" ? "Free" : "Paid"} ·{" "}
              {(selectedModel.contextWindow / 1000).toFixed(0)}K context
              {isDemoModel(selectedModel) && " · demo"}
            </p>
          ) : null}

          <label className="mb-1.5 mt-4 block text-[11px] font-semibold uppercase tracking-wider text-canvas-muted">
            Prompt
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-canvas-border bg-canvas px-3 py-2 text-sm outline-none focus:border-accent"
          />

          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-canvas-muted">
                Max tokens
              </span>
              <input
                type="number"
                value={maxTokens}
                min={1}
                max={8192}
                onChange={(e) => setMaxTokens(Number(e.target.value) || 200)}
                className="w-full rounded-lg border border-canvas-border bg-canvas px-3 py-1.5 text-sm outline-none focus:border-accent"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-canvas-muted">
                Temperature
              </span>
              <input
                type="number"
                value={temperature}
                min={0}
                max={2}
                step={0.1}
                onChange={(e) => setTemperature(Number(e.target.value) || 0)}
                className="w-full rounded-lg border border-canvas-border bg-canvas px-3 py-1.5 text-sm outline-none focus:border-accent"
              />
            </label>
            <label className="flex items-end gap-2 pb-1.5">
              <input
                type="checkbox"
                checked={stream}
                onChange={(e) => setStream(e.target.checked)}
                className="h-4 w-4 accent-accent"
              />
              <span className="text-xs text-canvas-muted">Stream (SSE)</span>
            </label>
          </div>

          <button
            className="btn-primary mt-4 w-full justify-center"
            onClick={send}
            disabled={sending || !prompt.trim()}
          >
            {sending ? "Sending…" : "Send request"}
          </button>
          {error && (
            <div className="mt-3 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
              {error}
            </div>
          )}
        </section>

        {/* ── Response + route ──────────────────────────────── */}
        <div className="space-y-5">
          <section className="panel p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold">Response</h2>
              {route && (
                <span className="text-xs text-canvas-muted">
                  {formatMs(route.latencyMs)}
                  {route.tokens.in != null &&
                    ` · ${route.tokens.in}/${route.tokens.out ?? "?"} tok`}
                  {route.cacheHit === "hit" && " · cache hit"}
                </span>
              )}
            </div>
            {loading ? (
              <SkeletonRows rows={3} />
            ) : output ? (
              <pre className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-lg bg-canvas p-3 text-sm leading-relaxed">
                {output}
              </pre>
            ) : (
              <EmptyState
                title="No response yet"
                body="Choose a model and send a request — the response and its route appear here."
              />
            )}
          </section>

          {route && (
            <section className="panel p-4">
              <SectionHeader title="NEXUS route" />
              <div className="space-y-2.5 text-sm">
                <div className="flex items-center gap-2">
                  {selectedProvider && (
                    <ProviderMark
                      letter={selectedProvider.definition.branding.letter}
                      accent={selectedProvider.definition.branding.accent}
                      size={22}
                    />
                  )}
                  <span className="font-medium">
                    {selectedProvider?.name ?? "Unknown provider"}
                  </span>
                  {route.providerModelId && (
                    <span className="text-canvas-muted">/ {route.providerModelId}</span>
                  )}
                  {selectedProvider?.providerTypeId.startsWith("mock-") && (
                    <span className="chip-muted !text-[10px]">DEMO</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 text-xs text-canvas-muted">
                  <span className="chip-muted">
                    {route.row?.fallbackCount
                      ? `${route.row.fallbackCount} fallback${route.row.fallbackCount === 1 ? "" : "s"}`
                      : "0 fallbacks"}
                  </span>
                  <span className="chip-muted">{formatMs(route.latencyMs)}</span>
                  <span className={route.row?.freeTier ? "chip-ok" : "chip-muted"}>
                    {route.row?.freeTier ? "free" : route.row ? "paid" : "…"}
                  </span>
                  {route.row && (
                    <span className="chip-muted">
                      cost {route.row.costUsd != null ? `$${route.row.costUsd.toFixed(6)}` : "$0"}
                    </span>
                  )}
                </div>
                {attempts.length > 0 && (
                  <div className="space-y-1 text-xs">
                    {attempts.map((a, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Dot tone={a.status === "success" ? "ok" : "danger"} />
                        <span className="mono text-canvas-muted">
                          attempt {a.attemptNumber} · {a.providerTypeId} · {a.httpStatus ?? "—"} ·{" "}
                          {formatMs(a.latencyMs)}
                        </span>
                        {a.errorMessage && (
                          <span className="truncate text-danger">{a.errorMessage}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {explanation?.reasons && (
                  <ul className="space-y-1 border-t border-canvas-border pt-2.5 text-xs text-canvas-muted">
                    {explanation.reasons.slice(0, 4).map((r) => (
                      <li key={r} className="flex gap-1.5">
                        <span className="text-accent">✓</span>
                        {r}
                      </li>
                    ))}
                  </ul>
                )}
                {route.requestId && (
                  <Link
                    to={`/requests?id=${route.requestId}`}
                    className="inline-block pt-1 text-xs text-accent hover:underline"
                  >
                    View full request trace →
                  </Link>
                )}
              </div>
            </section>
          )}

          <section className="panel">
            <div className="panel-header">
              <h2 className="text-sm font-semibold">Use in your app</h2>
              <div className="flex gap-1">
                {(Object.keys(snippets) as Lang[]).map((l) => (
                  <button
                    key={l}
                    className={`chip cursor-pointer !py-0.5 text-xs ${lang === l ? "chip-accent" : "chip-muted"}`}
                    onClick={() => setLang(l)}
                  >
                    {snippets[l].label}
                  </button>
                ))}
              </div>
            </div>
            <div className="relative p-3">
              <pre className="overflow-x-auto rounded-lg bg-canvas p-3 text-xs leading-relaxed">
                {snippets[lang].code}
              </pre>
              <div className="absolute right-4 top-4">
                <CopyButton text={snippets[lang].code} label="Copy" />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
